/**
 * Buy-ins Spin & Go par site.
 * Utilisé pour proposer des buy-ins adaptés au site (nouvelle session, outil assistant Spin).
 * @see docs/SPIN_GO_BUYINS_PAR_SITE.md
 */

export interface SpinGoBuyInsForSite {
  values: number[];
  currency: string;
}

/** Alias de noms de site → clé utilisée dans SPIN_GO_BUYINS_BY_SITE (casse / variantes renvoyées par l’API bankrolls) */
const SITE_ALIASES: Record<string, string> = {
  'PokerStars.fr': 'Pokerstars.fr',
  'PokerStars.com': 'Pokerstars.com',
  'PartyPoker': 'Partypoker.com',
  '888poker': '888 Poker',
  'Unibet Poker': 'Unibet',
  'Betclic Poker': 'Betclic',
};

/** Liste des buy-ins par site (clé = nom du site tel qu’en base / bankroll) */
export const SPIN_GO_BUYINS_BY_SITE: Record<string, { values: number[]; currency: string }> = {
  'Winamax': { values: [0.25, 1, 2, 5, 10, 20, 50, 100, 250, 500], currency: 'EUR' },
  'Unibet': { values: [1, 2, 5, 10, 20, 50, 100], currency: 'EUR' },
  'Betclic': { values: [0.2, 1, 2, 5, 10, 20, 50, 100, 200], currency: 'EUR' },
  'PMU': { values: [1, 2, 5, 10, 20, 50, 100], currency: 'EUR' },
  'Partypoker.fr': { values: [1, 2, 5, 10, 20, 50, 100], currency: 'EUR' },
  'Partypoker.com': { values: [1, 2, 5, 10, 20, 50, 100], currency: 'EUR' },
  'Pokerstars.fr': { values: [0.5, 1, 2, 5, 10, 25, 50, 100, 250], currency: 'EUR' },
  'Pokerstars.com': { values: [0.25, 1, 3, 7, 15, 30, 60, 100, 200, 500, 1000], currency: 'USD' },
  'GGPoker': { values: [0.25, 1, 3, 10, 20, 50, 100, 200], currency: 'USD' },
  'ACR': { values: [0.25, 1, 5, 10, 25, 50, 100], currency: 'USD' },
};

/** Liste complète (tous les buy-ins de tous les sites), dédoublonnée et triée — fallback pour site inconnu */
const ALL_BUYIN_VALUES: number[] = (() => {
  const set = new Set<number>();
  Object.values(SPIN_GO_BUYINS_BY_SITE).forEach(({ values }) => values.forEach((v) => set.add(v)));
  return Array.from(set).sort((a, b) => a - b);
})();

function normalizeSiteName(siteName: string): string {
  const trimmed = (siteName || '').trim();
  return SITE_ALIASES[trimmed] ?? trimmed;
}

/**
 * Retourne les buy-ins et la devise pour un site donné.
 * Site connu → liste du site ; site inconnu → liste complète (fallback).
 */
export function getSpinGoBuyInsForSite(siteName: string): SpinGoBuyInsForSite {
  const key = normalizeSiteName(siteName);
  const entry = SPIN_GO_BUYINS_BY_SITE[key];
  if (entry) return { values: [...entry.values], currency: entry.currency };
  return { values: [...ALL_BUYIN_VALUES], currency: 'EUR' };
}

/**
 * Retourne uniquement les valeurs de buy-in pour un site (pour dropdowns).
 * Site connu → liste du site ; site inconnu → liste complète, dédoublonnée et triée.
 */
export function getSpinGoBuyInValuesForSite(siteName: string): number[] {
  return getSpinGoBuyInsForSite(siteName).values;
}

/**
 * Retourne le mix des buy-ins de plusieurs sites : union des valeurs, dédoublonnée et triée.
 * Utilisé quand l'utilisateur sélectionne plusieurs bankrolls (ex. outil Spin).
 */
export function getSpinGoBuyInValuesForSites(siteNames: string[]): number[] {
  if (siteNames.length === 0) return [];
  if (siteNames.length === 1) return getSpinGoBuyInValuesForSite(siteNames[0]);
  const set = new Set<number>();
  for (const name of siteNames) {
    getSpinGoBuyInValuesForSite(name).forEach((v) => set.add(v));
  }
  return Array.from(set).sort((a, b) => a - b);
}
